import React from "react";
import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, TextField, Typography, useMediaQuery } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Navigate, useNavigate } from "react-router-dom";
import SettingsDialog from "../SettingsDialog";
import PlanPaywall, { PLANS, formatPlanPrice } from "../billing/PlanPaywall";
import { identifyUser, resetUser } from "../../analytics/posthogClient";
import { sessionIsOnboarded } from "../../utils/onboardingStatus";

const SOLVER_API_URL = process.env.REACT_APP_SOLVER_API_URL || "http://localhost:3001";

export default function EmailVerificationGate({ children }) {
  const navigate = useNavigate();
  const AUTH_BTN_SX = {
    backgroundColor: "#56ECCF",
    borderColor: "#56ECCF",
    color: "#111",
    "&:hover": { backgroundColor: "rgb(45, 179, 155)", borderColor: "rgb(45, 179, 155)" },
    "&.Mui-disabled": { opacity: 0.55, color: "#444" },
  };
  const INPUT_SX = {
    "& .MuiOutlinedInput-input": { color: "#ffffff", fontSize: "0.72rem" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "rgba(255,255,255,0.85)" },
      "&:hover fieldset": { borderColor: "#ffffff" },
      "&.Mui-focused fieldset": { borderColor: "#ffffff" },
    },
  };

  const [loading, setLoading] = React.useState(true);
  const [authed, setAuthed] = React.useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [accountSection, setAccountSection] = React.useState("subscription");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [info, setInfo] = React.useState("");

  const [oldPassword, setOldPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [accountName, setAccountName] = React.useState("");
  const [sessionEmail, setSessionEmail] = React.useState("");
  const [profileNameInput, setProfileNameInput] = React.useState("");
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [feedbackLike, setFeedbackLike] = React.useState("");
  const [feedbackDislike, setFeedbackDislike] = React.useState("");
  const [feedbackOther, setFeedbackOther] = React.useState("");
  const [feedbackBusy, setFeedbackBusy] = React.useState(false);
  const [feedbackError, setFeedbackError] = React.useState("");
  const [feedbackInfo, setFeedbackInfo] = React.useState("");
  const [billing, setBilling] = React.useState(null);
  const [billingBusy, setBillingBusy] = React.useState(false);
  const [billingError, setBillingError] = React.useState("");
  const [billingInfo, setBillingInfo] = React.useState("");
  const [subscriptionGateOpen, setSubscriptionGateOpen] = React.useState(false);
  const [checkoutPlanId, setCheckoutPlanId] = React.useState("improver");
  const [checkoutMode, setCheckoutMode] = React.useState("subscribe"); // subscribe | change
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);
  const authedRef = React.useRef(false);
  const hasPaidAccessRef = React.useRef(false);
  const paywallPromptedRef = React.useRef(false);
  const TOP_RIGHT_BTN_SX = {
    backgroundColor: "transparent",
    borderColor: "#8f8f8f",
    color: "#9a9a9a",
    fontSize: "0.65rem",
    minHeight: 24,
    px: 1,
    "&:hover": { backgroundColor: "rgba(255,255,255,0.04)", borderColor: "#9d9d9d", color: "#b0b0b0" },
  };

  const fetchBillingStatus = async () => {
    try {
      const res = await fetch(`${SOLVER_API_URL}/api/billing/status`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        setBilling(data.billing || null);
        return data.billing || null;
      }
      setBilling(null);
      return null;
    } catch (_) {
      setBilling(null);
      return null;
    }
  };

  const refreshSession = async () => {
    const res = await fetch(`${SOLVER_API_URL}/api/auth/session`, { credentials: "include" });
    const data = await res.json().catch(() => ({}));
    const isAuthed = !!(res.ok && (data?.success || data?.verified));
    setAuthed(isAuthed);
    setOnboardingCompleted(isAuthed ? sessionIsOnboarded(data) : false);
    if (isAuthed) {
      setAccountName(String(data?.name || ""));
      setSessionEmail(String(data?.email || "").trim().toLowerCase());
      identifyUser({ email: data?.email, name: data?.name });
      await fetchBillingStatus();
    } else {
      setBilling(null);
    }
    return { isAuthed, data };
  };

  React.useEffect(() => {
    const run = async () => {
      try {
        await refreshSession();
      } catch (_) {}
      setLoading(false);
    };
    run();
  }, []);

  React.useEffect(() => {
    const onBillingRefresh = () => {
      refreshSession().catch(() => {});
    };
    const onPromoCaptured = () => {
      if (!authedRef.current) return;
      setBillingError("");
      setBillingInfo("");
      setSubscriptionGateOpen(true);
    };
    window.addEventListener("pm:billing-refresh", onBillingRefresh);
    window.addEventListener("pm:promo-captured", onPromoCaptured);
    return () => {
      window.removeEventListener("pm:billing-refresh", onBillingRefresh);
      window.removeEventListener("pm:promo-captured", onPromoCaptured);
    };
  }, []);

  // Keep PostHog person properties in sync once billing status is known.
  React.useEffect(() => {
    if (!authed || !sessionEmail) return;
    identifyUser({
      email: sessionEmail,
      name: accountName,
      subscriptionStatus: billing?.subscriptionStatus,
      plan: billing?.plan,
    });
  }, [authed, sessionEmail, accountName, billing?.subscriptionStatus, billing?.plan]);

  React.useEffect(() => {
    const onRequireAuth = () => {
      if (authed) return;
      navigate("/welcome");
    };
    window.addEventListener("pm:require-auth", onRequireAuth);
    return () => window.removeEventListener("pm:require-auth", onRequireAuth);
  }, [authed, navigate]);

  React.useEffect(() => {
    const features = billing?.hasFeatureAccess || {};
    const hasPaidAccess =
      typeof billing?.hasAccess === "boolean"
        ? !!billing.hasAccess
        : !!billing?.trialActive ||
          String(billing?.subscriptionStatus || "").toLowerCase() === "active" ||
          String(billing?.subscriptionStatus || "").toLowerCase() === "trialing";
    authedRef.current = !!authed;
    hasPaidAccessRef.current = hasPaidAccess;
    window.__pmBillingState = {
      authed,
      plan: billing?.plan || "none",
      subscriptionStatus: String(billing?.subscriptionStatus || "none"),
      subscriptionPlan: billing?.subscriptionPlan || null,
      hasPaidAccess,
      hasAccess: {
        trainer: !!features.trainer,
        trainerTurnRiver: !!features.trainerTurnRiver,
        play: !!features.play,
        playMaxOpponentLevel: Number(features.playMaxOpponentLevel || 0),
        solver: !!features.solver,
        solverPreflop: !!features.solverPreflop,
        analyse: !!features.analyse,
        referral: !!features.referral,
      },
      trialActive: !!billing?.trialActive,
      trialEndsAt: billing?.trialEndsAt || null,
      trialHoursRemaining: billing?.trialHoursRemaining ?? null,
      paywallRequired: !!billing?.paywallRequired,
    };
    window.__pmOpenSubscriptionGate = (opts = {}) => {
      if (!authedRef.current) {
        navigate("/welcome");
        return;
      }
      const planId = String(opts?.planId || opts?.plan || "").toLowerCase();
      if (planId === "starter" || planId === "improver" || planId === "pro") {
        setCheckoutPlanId(planId);
      }
      const paid = String(billing?.subscriptionPlan || billing?.plan || "").toLowerCase();
      const hasPaidPlan = paid === "starter" || paid === "improver" || paid === "pro";
      setCheckoutMode(hasPaidPlan ? "change" : "subscribe");
      setBillingError("");
      setBillingInfo("");
      setSubscriptionGateOpen(true);
    };
  }, [authed, billing, navigate]);

  // Soft paywall: prompt once when trial ends; user can dismiss and keep browsing.
  React.useEffect(() => {
    if (!authed || !onboardingCompleted) return;
    if (billing?.paywallRequired) {
      if (!paywallPromptedRef.current) {
        paywallPromptedRef.current = true;
        setSubscriptionGateOpen(true);
      }
    } else {
      paywallPromptedRef.current = false;
    }
  }, [authed, onboardingCompleted, billing?.paywallRequired]);

  React.useEffect(() => {
    const onRequireSubscription = (event) => {
      const detail = event?.detail || {};
      if (typeof window.__pmOpenSubscriptionGate === "function") {
        window.__pmOpenSubscriptionGate(detail);
      }
    };
    window.addEventListener("pm:require-subscription", onRequireSubscription);
    return () => window.removeEventListener("pm:require-subscription", onRequireSubscription);
  }, []);

  const call = async (path, body = null) => {
    const res = await fetch(`${SOLVER_API_URL}${path}`, {
      method: body ? "POST" : "GET",
      credentials: "include",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data };
  };

  const doSaveName = async () => {
    setError("");
    setInfo("");
    const trimmed = String(profileNameInput || "").trim();
    if (trimmed.length < 2) return setError("Please enter a name (at least 2 characters).");
    setBusy(true);
    try {
      const { ok, data } = await call("/api/auth/profile/name", { name: trimmed });
      if (!ok || !data?.success) return setError(data?.error || "Could not save name.");
      setAccountName(String(data?.name || trimmed));
      setProfileNameInput("");
      setInfo("Name saved.");
    } catch (_) {
      setError("Could not contact server.");
    } finally {
      setBusy(false);
    }
  };

  const doChangePassword = async () => {
    setError("");
    setInfo("");
    setBusy(true);
    try {
      const { ok, data } = await call("/api/auth/password/change", { oldPassword, newPassword });
      if (!ok || !data?.success) return setError(data?.error || "Could not change password.");
      setInfo("Password changed.");
      setOldPassword("");
      setNewPassword("");
    } catch (_) {
      setError("Could not contact server.");
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => {
    await call("/api/auth/logout", {});
    resetUser();
    setAuthed(false);
    setOnboardingCompleted(false);
    setBilling(null);
    setAccountName("");
    setSessionEmail("");
    setOpen(false);
    setSubscriptionGateOpen(false);
    navigate("/welcome", { replace: true });
  };

  const doSendFeedback = async () => {
    setFeedbackError("");
    setFeedbackInfo("");
    const like = String(feedbackLike || "").trim();
    const dislike = String(feedbackDislike || "").trim();
    const otherThoughts = String(feedbackOther || "").trim();
    if (!like && !dislike && !otherThoughts) {
      setFeedbackError("Please enter feedback before sending.");
      return;
    }
    setFeedbackBusy(true);
    try {
      const { ok, data } = await call("/api/auth/feedback", { like, dislike, otherThoughts });
      if (!ok || !data?.success) {
        setFeedbackError(data?.error || "Could not send feedback.");
        return;
      }
      setFeedbackInfo("Feedback sent. Thank you.");
      setFeedbackLike("");
      setFeedbackDislike("");
      setFeedbackOther("");
    } catch (_) {
      setFeedbackError("Could not contact server.");
    } finally {
      setFeedbackBusy(false);
    }
  };

  const doCancelSubscription = async () => {
    setBillingError("");
    setBillingInfo("");
    setBillingBusy(true);
    try {
      const { ok, data } = await call("/api/billing/subscription/cancel", {});
      if (!ok || !data?.success) {
        setBillingError(data?.error || data?.details || "Could not cancel subscription.");
        return;
      }
      setCancelConfirmOpen(false);
      setBillingInfo(
        data?.currentPeriodEnd
          ? `Cancellation scheduled. You keep access until ${new Date(data.currentPeriodEnd).toLocaleDateString()}.`
          : "Cancellation scheduled. You keep access until the end of the billing period."
      );
      await fetchBillingStatus();
    } catch (_) {
      setBillingError("Could not contact billing service.");
    } finally {
      setBillingBusy(false);
    }
  };

  const planKey = String(billing?.plan || "none").toLowerCase();
  const subscriptionStatus = String(billing?.subscriptionStatus || "none").toLowerCase();
  const currentPaidPlan =
    subscriptionStatus === "active" || subscriptionStatus === "trialing"
      ? ["starter", "improver", "pro"].includes(planKey)
        ? planKey
        : String(billing?.subscriptionPlan || "").toLowerCase()
      : null;
  const trialDaysLeft = (() => {
    const ts = billing?.trialEndsAt ? new Date(billing.trialEndsAt).getTime() : NaN;
    if (!Number.isFinite(ts)) return null;
    return Math.max(0, Math.ceil((ts - Date.now()) / (24 * 60 * 60 * 1000)));
  })();
  const statusBadge =
    planKey === "trial" || (!!billing?.trialActive && subscriptionStatus !== "active")
      ? { label: `Trial ${trialDaysLeft ?? 0}d left`, color: "#f4a340" }
      : subscriptionStatus === "active"
        ? {
            label:
              planKey === "starter" || planKey === "improver" || planKey === "pro"
                ? planKey.charAt(0).toUpperCase() + planKey.slice(1)
                : "Subscribed",
            color: "#56ECCF",
          }
        : { label: "No plan", color: "#e35c5c" };
  const isMobilePhone = useMediaQuery("(max-width:767px)");
  const canUpgrade =
    !!authed &&
    !!onboardingCompleted &&
    subscriptionStatus !== "active" &&
    (!!billing?.trialActive || !!billing?.paywallRequired || planKey === "none" || planKey === "trial");
  const openUpgradePlans = (planId = "improver") => {
    setBillingError("");
    setBillingInfo("");
    setCheckoutPlanId(planId);
    setCheckoutMode(currentPaidPlan ? "change" : "subscribe");
    setSubscriptionGateOpen(true);
  };
  const onSelectAccountPlan = (planId) => {
    if (currentPaidPlan && planId === currentPaidPlan) {
      setBillingInfo("This is your current plan.");
      return;
    }
    openUpgradePlans(planId);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", backgroundColor: "#161616" }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!authed) {
    return <Navigate to="/welcome" replace />;
  }

  if (!onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <Box sx={{ position: "relative", minHeight: "100vh" }}>
      <Box>{children}</Box>

      <Box sx={{ position: "absolute", top: 12, right: { xs: 8, sm: 14 }, zIndex: 1800, display: "flex", gap: 0.75, alignItems: "center" }}>
        {authed && !isMobilePhone && (
          <Typography sx={{ color: statusBadge.color, fontSize: "0.75rem", fontWeight: 700, alignSelf: "center", mr: 1.1 }}>
            {statusBadge.label}
          </Typography>
        )}
        {authed && canUpgrade && !isMobilePhone && (
          <Button
            variant="contained"
            size="small"
            onClick={() => openUpgradePlans()}
            sx={{
              ...AUTH_BTN_SX,
              textTransform: "none",
              fontSize: "0.7rem",
              fontWeight: 800,
              minHeight: 26,
              px: 1.2,
              py: 0.2,
              boxShadow: "none",
            }}
          >
            Upgrade
          </Button>
        )}
        {authed && (
          <IconButton
            size="small"
            onClick={() => setSettingsOpen(true)}
            title="Settings"
            sx={{ color: "#9a9a9a", alignSelf: "center", "&:hover": { color: "#ffffff", backgroundColor: "rgba(255,255,255,0.06)" } }}
          >
            <SettingsOutlinedIcon sx={{ fontSize: 19 }} />
          </IconButton>
        )}
        {authed && (
          <Button
            variant="text"
            onClick={() => {
              setOpen(true);
              setAccountSection("subscription");
              setError("");
              setInfo("");
              if (billing?.paywallRequired) {
                setSubscriptionGateOpen(true);
              }
            }}
            sx={{
              ...TOP_RIGHT_BTN_SX,
              border: "none",
              minWidth: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
            }}
          >
            {isMobilePhone && (
              <Box
                component="span"
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  backgroundColor: statusBadge.color,
                  boxShadow: `0 0 0 1px rgba(255,255,255,0.25)`,
                }}
              />
            )}
            Account
          </Button>
        )}
      </Box>

      <PlanPaywall
        open={subscriptionGateOpen}
        onClose={() => setSubscriptionGateOpen(false)}
        trialEndPromoCode={billing?.trialEndPromoCode || "ZV3PGBRG"}
        trialEndPromoCodes={billing?.trialEndPromoCodes || null}
        trialActive={!!billing?.trialActive}
        initialPlan={checkoutPlanId}
        currentPlan={currentPaidPlan}
        mode={checkoutMode}
        hideHostedCheckout
        headlineOverride={checkoutMode === "change" ? "Change your plan" : undefined}
        subtitleOverride={
          checkoutMode === "change"
            ? "Enter your card details to confirm the switch."
            : undefined
        }
        name={accountName}
        onSubscribed={async () => {
          setSubscriptionGateOpen(false);
          await fetchBillingStatus();
        }}
      />

      <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ backgroundColor: "#1f1f1f", border: "1px solid #333" }}>
          <Typography sx={{ color: "#e2e2e2", fontSize: 16, fontWeight: 700, mb: 1 }}>
            Cancel subscription?
          </Typography>
          <Typography sx={{ color: "#bcbcbc", fontSize: 12, mb: 1.5 }}>
            You’ll keep access until the end of your current billing period. You won’t be charged again after that.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              onClick={() => setCancelConfirmOpen(false)}
              sx={{ color: "#d0d0d0", textTransform: "none" }}
            >
              Keep plan
            </Button>
            <Button
              variant="contained"
              disabled={billingBusy}
              onClick={doCancelSubscription}
              sx={{ backgroundColor: "#e35c5c", color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#c94a4a" } }}
            >
              {billingBusy ? "Cancelling..." : "Confirm cancel"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>


      <Dialog open={open && authed} onClose={() => setOpen(false)} fullScreen>
        <DialogContent sx={{ backgroundColor: "#1f1f1f", border: "1px solid #333", p: { xs: 1.5, md: 2.5 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Button
              variant="text"
              onClick={() => setOpen(false)}
              sx={{ color: "#d0d0d0", textTransform: "none", minWidth: "auto", px: 0.3, fontSize: 18, lineHeight: 1 }}
            >
              ←
            </Button>
            <Typography sx={{ color: "#e2e2e2", fontSize: 15, fontWeight: 700 }}>
              Account
            </Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "220px 1fr" }, gap: 2.2, alignItems: "start" }}>
            <Box sx={{ border: "1px solid #333", borderRadius: 1.5, p: 1, display: "flex", flexDirection: "column", gap: 0.8 }}>
              {[
                { id: "profile", label: "Profile" },
                { id: "subscription", label: "Subscription" },
                { id: "logout", label: "Logout" },
              ].map((item) => (
                <Button
                  key={item.id}
                  onClick={() => {
                    setAccountSection(item.id);
                    if (item.id === "subscription" && billing?.paywallRequired) {
                      setSubscriptionGateOpen(true);
                    }
                  }}
                  variant={accountSection === item.id ? "contained" : "outlined"}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontSize: 12,
                    borderColor: accountSection === item.id ? "#56ECCF" : "#555",
                    backgroundColor: accountSection === item.id ? "#56ECCF" : "transparent",
                    color: accountSection === item.id ? "#111" : "#d0d0d0",
                    "&:hover": {
                      borderColor: "#56ECCF",
                      backgroundColor: accountSection === item.id ? "#56ECCF" : "rgba(86,236,207,0.1)",
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
            <Box sx={{ border: "1px solid #333", borderRadius: 1.5, p: { xs: 1.2, md: 1.7 }, minHeight: 360 }}>
              {accountSection === "profile" && (() => {
                const profilePlanId =
                  currentPaidPlan ||
                  (["starter", "improver", "pro"].includes(planKey) ? planKey : null);
                const profilePlanMeta = profilePlanId
                  ? PLANS.find((p) => p.id === profilePlanId)
                  : null;
                const profilePlanName =
                  billing?.planName ||
                  profilePlanMeta?.name ||
                  (billing?.trialActive || planKey === "trial" ? "Trial" : null) ||
                  "None";
                const profileAmount =
                  billing?.planAmountUsd ??
                  profilePlanMeta?.price ??
                  (billing?.trialActive || planKey === "trial" ? 0 : null);
                const profileFeatures =
                  profilePlanMeta?.features ||
                  (billing?.trialActive || planKey === "trial"
                    ? PLANS.find((p) => p.id === "pro")?.features || []
                    : []);
                const startedRaw =
                  billing?.subscriptionStartedAt ||
                  billing?.lastSuccessfulPaymentAt ||
                  billing?.trialStartedAt ||
                  null;
                const endRaw = billing?.stripeCurrentPeriodEnd || billing?.trialEndsAt || null;
                const endLabel =
                  billing?.cancelAtPeriodEnd
                    ? "Ends"
                    : billing?.trialActive && subscriptionStatus !== "active"
                      ? "Trial ends"
                      : "Renews";
                const fmtDate = (raw) => {
                  if (!raw) return "-";
                  const d = new Date(raw);
                  return Number.isFinite(d.getTime()) ? d.toLocaleDateString() : "-";
                };
                return (
                  <Box sx={{ display: "grid", gap: 2, maxWidth: 560 }}>
                    <Box sx={{ display: "grid", gap: 1.2 }}>
                      <Typography sx={{ color: "#e2e2e2", fontSize: 13, fontWeight: 700 }}>
                        Name
                      </Typography>
                      <Typography sx={{ color: "#bcbcbc", fontSize: 11 }}>
                        {String(accountName || "").trim()
                          ? `Current: ${String(accountName).trim()}`
                          : "No name set yet."}
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Your name"
                        value={profileNameInput}
                        onChange={(e) => setProfileNameInput(e.target.value.slice(0, 60))}
                        sx={INPUT_SX}
                      />
                      <Button variant="contained" onClick={doSaveName} disabled={busy} sx={{ ...AUTH_BTN_SX, width: "fit-content" }}>
                        {busy ? "Saving..." : "Change name"}
                      </Button>
                    </Box>

                    <Box sx={{ borderTop: "1px solid #333", pt: 1.8, display: "grid", gap: 1 }}>
                      <Typography sx={{ color: "#e2e2e2", fontSize: 13, fontWeight: 700 }}>
                        Current subscription
                      </Typography>
                      <Typography sx={{ color: statusBadge.color, fontSize: 11, fontWeight: 700 }}>
                        Status: {statusBadge.label}
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "140px 1fr" },
                          gap: 0.6,
                          alignItems: "start",
                        }}
                      >
                        <Typography sx={{ color: "#888", fontSize: 11 }}>Plan</Typography>
                        <Typography sx={{ color: "#d0d0d0", fontSize: 12, fontWeight: 600 }}>
                          {profilePlanName}
                        </Typography>
                        <Typography sx={{ color: "#888", fontSize: 11 }}>Cost</Typography>
                        <Typography sx={{ color: "#d0d0d0", fontSize: 12 }}>
                          {profileAmount == null
                            ? "-"
                            : profileAmount === 0
                              ? "Free"
                              : `$${formatPlanPrice(profileAmount)} / mo`}
                        </Typography>
                        <Typography sx={{ color: "#888", fontSize: 11 }}>Started</Typography>
                        <Typography sx={{ color: "#d0d0d0", fontSize: 12 }}>
                          {fmtDate(startedRaw)}
                        </Typography>
                        <Typography sx={{ color: "#888", fontSize: 11 }}>{endLabel}</Typography>
                        <Typography sx={{ color: "#d0d0d0", fontSize: 12 }}>
                          {fmtDate(endRaw)}
                          {billing?.cancelAtPeriodEnd ? " (cancel scheduled)" : ""}
                        </Typography>
                      </Box>
                      {profileFeatures.length > 0 && (
                        <Box sx={{ mt: 0.4, display: "grid", gap: 0.35 }}>
                          <Typography sx={{ color: "#888", fontSize: 11, mb: 0.2 }}>
                            What&apos;s included
                          </Typography>
                          {profileFeatures.map((f) => (
                            <Typography key={f} sx={{ color: "#cfcfcf", fontSize: 11 }}>
                              ✓ {f}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      <Typography sx={{ color: "#888", fontSize: 11, mt: 0.4 }}>
                        Manage plans and cancel in the Subscription tab.
                      </Typography>
                    </Box>

                    <Box sx={{ borderTop: "1px solid #333", pt: 1.8, display: "grid", gap: 1.2 }}>
                      <Typography sx={{ color: "#e2e2e2", fontSize: 13, fontWeight: 700 }}>
                        Reset password
                      </Typography>
                      <TextField
                        size="small"
                        type="password"
                        placeholder="Old password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        sx={INPUT_SX}
                      />
                      <TextField
                        size="small"
                        type="password"
                        placeholder="New password (min 8 chars)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        sx={INPUT_SX}
                      />
                      <Button variant="contained" onClick={doChangePassword} disabled={busy} sx={{ ...AUTH_BTN_SX, width: "fit-content" }}>
                        {busy ? "Saving..." : "Change password"}
                      </Button>
                    </Box>
                  </Box>
                );
              })()}
              {accountSection === "subscription" && (
                <Box sx={{ display: "grid", gap: 1.2, maxWidth: 720 }}>
                  <Typography sx={{ color: statusBadge.color, fontSize: 12, fontWeight: 700 }}>
                    Status: {statusBadge.label}
                  </Typography>
                  <Typography sx={{ color: "#d0d0d0", fontSize: 11 }}>
                    Plan: {billing?.plan || "none"}
                    {billing?.trialEndsAt
                      ? ` · Trial ends ${new Date(billing.trialEndsAt).toLocaleString()}`
                      : ""}
                    {billing?.cancelAtPeriodEnd && billing?.stripeCurrentPeriodEnd
                      ? ` · Cancels ${new Date(billing.stripeCurrentPeriodEnd).toLocaleDateString()}`
                      : ""}
                  </Typography>
                  <Typography sx={{ color: "#bcbcbc", fontSize: 11, mt: 0.4 }}>
                    {currentPaidPlan
                      ? "Upgrade or downgrade anytime. Click a different plan to enter card details and switch."
                      : billing?.trialActive
                        ? "You are on the free trial. Pick a plan below to subscribe."
                        : "Choose a plan below to restore access."}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    {PLANS.map((plan) => {
                      const isCurrent = currentPaidPlan === plan.id;
                      return (
                        <Box
                          key={plan.id}
                          onClick={() => onSelectAccountPlan(plan.id)}
                          sx={{
                            cursor: isCurrent ? "default" : "pointer",
                            backgroundColor: "#191919",
                            border: `2px solid ${isCurrent ? "#56ECCF" : plan.featured ? "#B99E4F" : "#333"}`,
                            borderRadius: 1.5,
                            p: 1.4,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.55,
                            "&:hover": isCurrent
                              ? {}
                              : { borderColor: "#56ECCF", backgroundColor: "#1f1f1f" },
                          }}
                        >
                          {isCurrent ? (
                            <Typography sx={{ color: "#56ECCF", fontSize: 10, fontWeight: 800, letterSpacing: "0.04em" }}>
                              CURRENT
                            </Typography>
                          ) : plan.featured ? (
                            <Typography sx={{ color: "#B99E4F", fontSize: 10, fontWeight: 800, letterSpacing: "0.04em" }}>
                              POPULAR
                            </Typography>
                          ) : null}
                          <Typography sx={{ color: "#fff", fontSize: 15, fontWeight: 800 }}>{plan.name}</Typography>
                          <Typography sx={{ color: "#56ECCF", fontSize: 20, fontWeight: 800, lineHeight: 1.1 }}>
                            ${formatPlanPrice(plan.price)}
                            <Box component="span" sx={{ color: "#888", fontSize: 11, fontWeight: 600 }}> / mo</Box>
                          </Typography>
                          {plan.features.slice(0, 4).map((f) => (
                            <Typography key={f} sx={{ color: "#cfcfcf", fontSize: 11 }}>✓ {f}</Typography>
                          ))}
                          {!isCurrent && (
                            <Typography sx={{ color: "#56ECCF", fontSize: 11, fontWeight: 700, mt: 0.4 }}>
                              {currentPaidPlan ? "Switch plan →" : "Select →"}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  {currentPaidPlan && (
                    <Box sx={{ mt: 1.2, display: "grid", gap: 0.8 }}>
                      {billing?.cancelAtPeriodEnd ? (
                        <Typography sx={{ color: "#f4a340", fontSize: 11 }}>
                          Cancellation is already scheduled. You keep access until the end of the billing period.
                        </Typography>
                      ) : (
                        <>
                          <Typography sx={{ color: "#bcbcbc", fontSize: 11 }}>
                            Cancel anytime. You keep access until the end of your current billing period.
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={() => setCancelConfirmOpen(true)}
                            disabled={billingBusy}
                            sx={{
                              borderColor: "#e35c5c",
                              color: "#e35c5c",
                              textTransform: "none",
                              fontSize: 12,
                              width: "fit-content",
                            }}
                          >
                            Cancel subscription
                          </Button>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              )}
              {accountSection === "logout" && (
                <Box sx={{ display: "grid", gap: 1.2, maxWidth: 520 }}>
                  <Typography sx={{ color: "#d0d0d0", fontSize: 12 }}>
                    Sign out of your PokerMoose account on this device.
                  </Typography>
                  <Button variant="outlined" onClick={doLogout} sx={{ borderColor: "#e35c5c", color: "#e35c5c", textTransform: "none", width: "fit-content" }}>
                    Logout
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          {billingError && <Typography sx={{ color: "#ff7d7d", fontSize: 11, mt: 1.2 }}>{billingError}</Typography>}
          {billingInfo && <Typography sx={{ color: "#9ecbff", fontSize: 11, mt: 1.2 }}>{billingInfo}</Typography>}
          {error && <Typography sx={{ color: "#ff7d7d", fontSize: 11, mt: 0.8 }}>{error}</Typography>}
          {info && <Typography sx={{ color: "#9ecbff", fontSize: 11, mt: 0.8 }}>{info}</Typography>}
        </DialogContent>
      </Dialog>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />


      <Dialog open={feedbackOpen && authed} onClose={() => setFeedbackOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ backgroundColor: "#1f1f1f", border: "1px solid #333" }}>
          <Typography sx={{ color: "#e2e2e2", fontSize: 16, fontWeight: 700, mb: 1 }}>
            Feedback
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              size="small"
              placeholder="What do you like about the app?"
              value={feedbackLike}
              onChange={(e) => setFeedbackLike(e.target.value)}
              sx={INPUT_SX}
              disabled={feedbackBusy}
            />
            <TextField
              size="small"
              placeholder="What do you dislike about the app?"
              value={feedbackDislike}
              onChange={(e) => setFeedbackDislike(e.target.value)}
              sx={INPUT_SX}
              disabled={feedbackBusy}
            />
            <TextField
              size="small"
              placeholder="Any other thoughts?"
              value={feedbackOther}
              onChange={(e) => setFeedbackOther(e.target.value)}
              sx={INPUT_SX}
              disabled={feedbackBusy}
              multiline
              minRows={4}
            />
            <Button variant="contained" onClick={doSendFeedback} disabled={feedbackBusy} sx={AUTH_BTN_SX}>
              {feedbackBusy ? "Sending..." : "Send"}
            </Button>
          </Box>
          {feedbackError && <Typography sx={{ color: "#ff7d7d", fontSize: 11, mt: 1 }}>{feedbackError}</Typography>}
          {feedbackInfo && <Typography sx={{ color: "#9ecbff", fontSize: 11, mt: 1 }}>{feedbackInfo}</Typography>}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

